import $ from 'jquery';

import AppleView from 'chairisher/view/apple';
import AuthenticationView from 'chairisher/view/authentication';
import CookieUtils, { CookieName } from 'chairisher/util/cookie';
import FacebookView from 'chairisher/view/facebook';
import FavoriteProductView from 'chairisher/view/product/favorite';
import FavoritesNavMenu from 'chairisher/view/nav/favoritesnav';
import UriUtils from 'chairisher/util/uri';

import updateAccountDataContext from 'chairisher/util/accountdata';
import renderGoogleSignin from 'chairisher/view/socialauth/google';

import {
    trackAccountCreated,
    trackSuccessfulAuthorizationForAction,
    trackUnauthorizedAction,
    trackUnsuccessfulAuthorizationForAction,
} from 'chairisher/analytics/auth';
import { trackAccountCreatedForPRT } from 'chairisher/thirdparty/pinterestretargeting';
import { handleAsyncError } from 'chairisher/util/logging';
import { isUnauthorized } from 'chairisher/util/status';
import { isFunctionType } from 'chairisher/util/type';

/**
 * Automatically include the CSRF header in jQuery XHR.
 *
 * @param {string} method The HTTP method being used
 * @returns {boolean} True indicates the HTTP method does not need CSRF protection
 */
function csrfSafeMethod(method) {
    // these HTTP methods do not require CSRF protection
    return /^(GET|HEAD|OPTIONS|TRACE)$/.test(method);
}

$.ajaxSetup({
    beforeSend(xhr, settings) {
        if (!csrfSafeMethod(settings.type)) {
            const csrfToken = CookieUtils.getValueFromCookie(CookieName.CSRF_TOKEN);
            xhr.setRequestHeader('X-CSRFToken', csrfToken);

            if (this.data && isFunctionType(this.data.split)) {
                const dataParts = this.data.split('&');
                for (let i = 0; i < dataParts.length; i += 1) {
                    if (dataParts[i].indexOf('csrfmiddlewaretoken=') !== -1) {
                        dataParts[i] = `csrfmiddlewaretoken=${csrfToken}`;
                    }
                }

                this.data = dataParts.join('&');
            }
        }
    },
});

//
// $.ajaxPrefilter is used to handle/modify options before the request is processed by $.ajax.
// http://api.jquery.com/jquery.ajaxprefilter/
$.ajaxPrefilter((options, originalOptions, jqXHR) => {
    // Don't do anything if the authUrl custom option is not
    // included in the options passed to $.ajax. This prevents
    // the authentication modal from being bound infinitely
    if (!originalOptions.authUrl) {
        return undefined;
    }

    // Declare a new deferred object to handle fail callbacks
    const $deferred = $.Deferred();

    // Return normally if the request works. Since $.ajaxPrefilter is
    // called before $.ajax returns, this .done will be called before
    // any other .done that is added elsewhere. Below the XHR object is
    // added as a promise to $deferred, so the original will be called
    // as soon as $deferred is resolved.
    jqXHR.done($deferred.resolve);

    // Inject this callback before others to ensure the account is authenticated.
    jqXHR.fail((...args) => {
        // If the error is not related to authentication, reject $deferred
        // and call other registered .fail callbacks registered w/the original args
        if (!isUnauthorized(jqXHR.status)) {
            $deferred.rejectWith(jqXHR, args);
        } else {
            // The user is not authenticated so display the appropriate
            // AccountQuickCreateView. The .done callback registered by the code
            // that made the original $.ajax call, will not be called until
            // $deferred is resolved
            const authView = new AuthenticationView({
                formSelector: '.js-auth-quick',
            });
            authView
                .display({
                    url: options.authUrl,
                })
                .done(() => {
                    const variation = UriUtils.extractParamFromUri(options.authUrl, 'v') || 'unknown';

                    AppleView.loadAuthButton(); // for apple sign in button
                    FacebookView.bind(); // for facebook connect login button
                    renderGoogleSignin();

                    trackUnauthorizedAction(variation);

                    const $modal = $(authView.getModalSelector());

                    $modal.on('submit', 'form', function handleSubmitForm(e) {
                        e.preventDefault();
                        const $form = $(this);
                        $form.find('.btn-default').prop('disabled', true);

                        authView.submit(
                            $form.attr('action'),
                            $form.serialize(),
                            async (data) => {
                                authView.hide();

                                try {
                                    const accountData = await updateAccountDataContext([
                                        'BUYER_TYPE_CODE',
                                        'GUID',
                                        'IS_AUTHENTICATED',
                                        'IS_EMAIL_CONFIRMED',
                                        'IS_GREENLIT_SELLER',
                                        'IS_STAFF',
                                        'IS_TRADE',
                                        'favorite_nav_items',
                                    ]);
                                    if (accountData?.favorite_nav_items) {
                                        FavoritesNavMenu.bind(accountData);
                                    }
                                } catch (error) {
                                    handleAsyncError(error);
                                }

                                if (data.was_account_created) {
                                    trackAccountCreatedForPRT();
                                    trackAccountCreated();
                                }

                                trackSuccessfulAuthorizationForAction(variation);

                                FavoriteProductView.updateFavoriteProductIds()
                                    .done(() => {
                                        FavoriteProductView.drawButtons();
                                    })
                                    .then(() => {
                                        FavoriteProductView.updateFavoriteCount(data.num_favorites);

                                        // Resend the original $.ajax request. On success, resolve
                                        // $deferred so the original .done is called. On failure reject
                                        // $deferred so the original .fail is called. Since options still
                                        // contains the custom authUrl option, if the user is still not
                                        // authenticated for whatever reason, $.ajaxPrefilter will handle it
                                        $.ajax(options).then($deferred.resolve, $deferred.reject);
                                    });
                            },
                            (data) => {
                                trackUnsuccessfulAuthorizationForAction(variation);

                                // If the authentication form is submitted and comes back with a 4XX or 5XX
                                // status code, inject the returned HTML with errors. Since $.deferred
                                // has not been rejected or resolved yet, there's no danger of the original
                                // .done or .fail being called
                                authView.setFormHtml(data.authentication_form_html);

                                // re-bind the apple and facebook button
                                AppleView.loadAuthButton();
                                FacebookView.bind();
                                renderGoogleSignin();

                                // re-enable submit button if failure
                                $form.find('.btn-default').prop('disabled', false);
                            },
                        );
                    });

                    // If the modal is dismissed without authenticating,
                    // reject $deferred so the original .fail can be called
                    $modal.on('hidden.bs.modal', () => {
                        if ($deferred.state() === 'pending' && !authView.getDidAttemptAuthentication()) {
                            // only execute this code if the deferred object is neither resolved nor rejected
                            const xhr = jqXHR; // this is to avoid the ESLint error "no-param-reassign" when calling delete jqXHR.status;
                            delete xhr.status; // remove the status so we don't get duplicative errors
                            $deferred.rejectWith(jqXHR, args);
                            trackUnsuccessfulAuthorizationForAction(variation);
                        }
                    });
                });
        }
    });

    // Prevent jqXHR's promise callbacks from being executed
    // until $deferred's have been resolved or rejected
    return $deferred.promise(jqXHR);
});

//
// jQuery Plugins
//

/**
 * Throttles execution of a callback on rapidly fired events
 *
 * @param {Function} callback
 * @returns {Function}
 */
function throttle(callback) {
    let isRunning = false;

    return function throttled(...args) {
        if (isRunning) {
            return;
        }

        isRunning = true;

        window.requestAnimationFrame(function handleRequestAnimationFrame() {
            callback.apply(this, args);
            isRunning = false;
        });
    };
}

/**
 * Executes a function on resize, but on a much more throttled basis
 *
 * @param {Function} fn The function to execute
 * @param {string=} eventNamespace The namespace to add to the resize event
 * @returns {*}
 */
$.fn.smartresize = function smartresize(fn, eventNamespace) {
    const eventName = eventNamespace ? `resize.${eventNamespace}` : 'resize';

    if (fn) {
        return this.on(eventName, throttle(fn));
    }
    return this.trigger(eventName);
};

/**
 * Executes a function on scroll, but on a much more throttled basis
 *
 * @param {Function} fn The function to execute
 * @param {string=} eventNamespace The namespace to add to the scroll event
 * @returns {*}
 */
$.fn.smartscroll = function smartscroll(fn, eventNamespace) {
    const eventName = eventNamespace ? `scroll.${eventNamespace}` : 'scroll';

    if (fn) {
        return this.on(eventName, throttle(fn));
    }
    return this.trigger(eventName);
};

/**
 * Executes a function on scrollend, with a debounced scroll fallback where not supported.
 *
 * @param {Function} fn The function to execute when scrolling ends
 * @param {string=} eventNamespace The namespace to add to the event
 * @returns {*}
 */
$.fn.smartscrollend = function smartscrollend(fn, eventNamespace) {
    if ('onscrollend' in window) {
        const scrollEndEventName = eventNamespace ? `scrollend.${eventNamespace}` : 'scrollend';
        return this.on(scrollEndEventName, fn);
    }

    // Fallback for browsers without native scrollend
    const eventName = eventNamespace ? `scroll.${eventNamespace}` : 'scroll';
    const delayMs = 150;
    let timeoutId = null;
    const handler = function debounceSmartScrollEnd(...args) {
        if (timeoutId !== null) {
            clearTimeout(timeoutId);
        }
        timeoutId = setTimeout(() => {
            timeoutId = null;
            fn.apply(this, args);
        }, delayMs);
    };
    return this.on(eventName, handler);
};

/**
 * A simple jQuery plugin to warn a user of unsaved modifications to a form. Can be called multiple times without
 * issue since we unbind before binding.
 *
 * @example:
 * // activates unsaved content modal
 * $('#myForm').warnOnUnload()
 */
function initializeWarnOnUnloadHelper() {
    let shouldWarn = false;

    $(window).on('beforeunload', (e) => {
        if (!shouldWarn) {
            return undefined;
        }

        e.preventDefault();

        return '';
    });

    /**
     * Responds to a form change.
     */
    const handleFormChange = function handleFormChange() {
        shouldWarn = true;
    };

    /**
     * Responds to a form submission attempt by disabling any warnings.
     * Note that this is not dependent on which form changed.
     */
    const handleFormSubmit = function handleFormSubmit() {
        shouldWarn = false;
    };

    $.fn.warnOnUnload = function warnOnUnload() {
        const form = $(this);

        shouldWarn = false;

        // Detect the first change.
        form.off('change.warnOnUnload').one('change.warnOnUnload', handleFormChange);

        // Detect submissions.
        form.off('submit.warnOnUnload').on('submit.warnOnUnload', handleFormSubmit);

        return form;
    };
}
initializeWarnOnUnloadHelper();

/**
 * A jQuery plugin to automatically invoke a disabled loading state on a form's
 * buttons on submit.
 *
 * @requires bootstrap-button.js
 */
function initializeButtonLoadingHelper() {
    const getAllLoadingButtons = function getAllLoadingButtons(parent) {
        return parent.find('.btn[data-loading-text]');
    };

    const disableAll = function disableAll(form) {
        const loadingButtons = getAllLoadingButtons(form);
        loadingButtons.button('loading');
    };

    /**
     * Responds to form submission by setting the loading state of all buttons
     * with explicit loading text.
     */
    const handleFormSubmit = function handleFormSubmit(e) {
        // TODO(nate): How can we enforce this handler to be called last?
        if (!e.isDefaultPrevented()) {
            disableAll($(e.target));
        }
    };

    const resetAll = function resetAll(form) {
        const loadingButtons = getAllLoadingButtons(form);
        loadingButtons.button('reset');
    };

    $.fn.buttonLoadingHelper = function buttonLoadingHelper() {
        const form = $(this);

        // Detect submissions.
        form.on('submit', handleFormSubmit);

        return form;
    };

    $.fn.disableLoadingButtons = function disableLoadingButtons() {
        const form = $(this);

        disableAll(form);

        return form;
    };

    $.fn.resetLoadingButtons = function resetLoadingButtons() {
        const form = $(this);

        resetAll(form);

        return form;
    };
}
initializeButtonLoadingHelper();
